import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronsUpDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api, errMsg, today, fmtUS } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Field, Select } from "@/components/common";
import { BackButton } from "@/components/BackButton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const SRC = ["Pharmacy call", "Pharmacy portal", "Pharmacy fax", "Other"];
const PREP = ["Descovy", "Truvada", "Generic Truvada", "Apretude", "Yeztugo"];
const HIV = ["Biktarvy", "Dovato", "Cabenuva"];
const MEDS = [...PREP, ...HIV];
const FACTOR = { Apretude: 2, Yeztugo: 6 };
const NEW_RX = "__new__";

function MedPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="inp flex items-center justify-between text-left" data-testid="fill-medication"><span>{value || "Select medication"}</span><ChevronsUpDown size={13} className="opacity-50" /></button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search medication…" data-testid="fill-medication-search" />
          <CommandList><CommandEmpty>No medication found</CommandEmpty>
            {[["PrEP Medications", PREP], ["HIV Treatment Medications", HIV]].map(([h, list]) => (
              <CommandGroup key={h} heading={h}>{list.map((m) => <CommandItem key={m} value={m} onSelect={() => { onChange(m); setOpen(false); }} data-testid={`fill-medication-option-${m.replace(/\s/g, "-")}`}>{m}</CommandItem>)}</CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FillRow({ i, r, set, isInj, rxOptions, mismatch }) {
  return (
    <div className="rounded-lg border bg-[var(--panel)] p-2.5" data-testid={`fill-row-${i + 1}`}>
      <Field label={`Fill Date ${i + 1}`} required={i === 0}><input type="date" className="inp h-9" value={r.date} onChange={(e) => set({ date: e.target.value })} data-testid={`fill-date-${i + 1}`} /></Field>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        <select className="inp h-8 text-xs" value={r.medication} onChange={(e) => set({ medication: e.target.value })} data-testid={`fill-row-med-${i + 1}`}>{[["PrEP Medications", PREP], ["HIV Treatment Medications", HIV]].map(([h, list]) => <optgroup key={h} label={h}>{list.map((m) => <option key={m}>{m}</option>)}</optgroup>)}</select>
        {isInj && <label className="flex items-center gap-1.5 text-[11px]"><input type="checkbox" checked={!!r.administered} onChange={(e) => set({ administered: e.target.checked })} data-testid={`fill-row-admin-${i + 1}`} />Injection administered</label>}
      </div>
      {mismatch && r.date && (
        <select className="inp mt-1.5 h-8 w-full border-amber-400 text-xs" value={r.prescription_id || ""} onChange={(e) => set({ prescription_id: e.target.value })} data-testid={`fill-row-rx-${i + 1}`}>
          <option value="">Select {r.medication} prescription *</option>
          {rxOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          <option value={NEW_RX}>Create new {r.medication} prescription</option>
        </select>
      )}
    </div>
  );
}

export function FillDialog({ open, onClose, patient, onSaved, prescriptions = [], onViewExisting }) {
  const { meta } = useApp();
  const [f, setF] = useState({});
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);
  const lock = useRef(false);
  const rxs = [...prescriptions].filter((r) => r.status !== "Cancelled").sort((a, b) => (b.sent_date || "").localeCompare(a.sent_date || ""));
  const rxFor = (m) => rxs.filter((r) => r.medication === m).map((r) => ({ value: r.id, label: `Rx ${fmtUS(r.sent_date)}${r.legacy_slot ? ` (${r.legacy_slot})` : ""}` }));
  const blank = (med) => Array.from({ length: 6 }, (_, i) => ({ date: i === 0 ? today() : "", medication: med, administered: false, prescription_id: "" }));
  useEffect(() => {
    if (!open) return;
    const med = MEDS.includes(patient.medication) ? patient.medication : "Descovy";
    setF({ medication: med, prescription_id: rxs.find((r) => r.medication === med)?.id || "", pharmacy: patient.pharmacy_filled || patient.pharmacy_sent || "", notes: "", confirmed: true, confirmation_source: "Pharmacy call" });
    setRows(blank(med));
    setExisting(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patient]);
  const injectable = (m) => (meta?.medications || []).find((x) => x.name === m)?.type === "injectable" || !!FACTOR[m];
  const topRx = rxs.find((r) => r.id === f.prescription_id);
  const mismatch = (r) => !!f.prescription_id && topRx && r.medication !== topRx.medication;
  const setTop = (patch) => {
    const nf = { ...f, ...patch };
    if (patch.medication) nf.prescription_id = rxs.find((r) => r.medication === patch.medication)?.id || "";
    setF(nf);
    setRows(rows.map((r) => ({ ...r, ...(patch.medication ? { medication: patch.medication, prescription_id: "" } : {}) })));
  };
  const setRow = (i, patch) => setRows(rows.map((r, k) => (k === i ? { ...r, ...patch, ...(patch.medication ? { prescription_id: "" } : {}) } : r)));
  const filled = rows.filter((r) => r.date);
  const count = filled.length;
  const done = filled.filter((r) => r.date <= today()).length;
  const sched = count - done;
  const save = async (updateExisting = false) => {
    if (!rows[0].date) return toast.error("Fill Date 1 is required");
    const keys = filled.map((r) => `${r.medication}|${r.date}`);
    if (new Set(keys).size !== keys.length) return toast.error("The same medication and date was entered more than once");
    if (filled.some((r) => mismatch(r) && !r.prescription_id)) return toast.error("Select or create a matching prescription for fills with a different medication");
    if (f.confirmed && !f.confirmation_source) return toast.error("Select how the pharmacy confirmed the fill");
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      const body = { pharmacy: f.pharmacy, notes: f.notes, confirmed: f.confirmed, confirmation_source: f.confirmed ? f.confirmation_source : null, update_existing: updateExisting,
        rows: filled.map((r) => {
          const rx = mismatch(r) ? r.prescription_id : (r.medication === topRx?.medication ? f.prescription_id : "");
          return { date: r.date, medication: r.medication, administered: injectable(r.medication) && r.administered,
            prescription_id: rx && rx !== NEW_RX ? rx : null, create_prescription: rx === NEW_RX };
        }) };
      const { data } = await api.post(`/patients/${patient.id}/fills/batch`, body);
      toast.success(`${data.created} fill${data.created === 1 ? "" : "s"} recorded${data.updated ? `, ${data.updated} existing fill${data.updated === 1 ? "" : "s"} updated` : ""}${data.administrations ? `, ${data.administrations} injection administration(s) recorded` : ""}`);
      setExisting(null);
      onSaved();
      onClose();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 409 && det?.code === "existing_fill") setExisting(det.existing);
      else toast.error(errMsg(e));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="fill-dialog">
        <BackButton label="Back to Patient Profile" onClick={onClose} testid="fill-back-btn" className="-ml-1.5 self-start" />
        <DialogHeader><DialogTitle className="text-[#20336F] dark:text-blue-200">Record Prescription Fill</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Patient ID"><input className="inp" disabled value={patient.patient_id} data-testid="fill-patient-id" /></Field>
          <Field label="Medication"><MedPicker value={f.medication} onChange={(m) => setTop({ medication: m })} /></Field>
          <Field label="Associated Prescription" className="sm:col-span-2"><Select value={f.prescription_id} onChange={(v) => setF({ ...f, prescription_id: v })} placeholder="None / not linked" testid="fill-prescription-select" options={rxFor(f.medication)} /></Field>
          {rows.map((r, i) => <FillRow key={i} i={i} r={r} set={(p) => setRow(i, p)} isInj={injectable(r.medication)} rxOptions={rxFor(r.medication)} mismatch={mismatch(r)} />)}
          <div className="sm:col-span-2" data-testid="fill-total">
            <span className="inline-block rounded-lg bg-[#EAF4FF] px-3 py-1.5 text-sm text-[#20336F] dark:bg-white/5 dark:text-blue-200">Total Recorded Fills: <b data-testid="fill-total-count">{count}</b>{sched > 0 && <span className="ml-1 text-xs text-blue-700" data-testid="fill-scheduled-count">({sched} future — counted, awaiting dispensing confirmation)</span>}</span>
          </div>
          <p className="text-xs text-[var(--muted-ink)] sm:col-span-2" data-testid="fill-supply-note">Every saved fill date counts as one Rx Filled, for every medication (past, today or future). Pharmacy confirmation is tracked separately. Blank dates are not saved; refill numbers are assigned automatically. Changing the medication here does not change the patient's current medication.</p>
          <Field label="Pharmacy" className="sm:col-span-2"><input className="inp" value={f.pharmacy || ""} onChange={(e) => setF({ ...f, pharmacy: e.target.value })} data-testid="fill-pharmacy-input" /></Field>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={!!f.confirmed} onChange={(e) => setF({ ...f, confirmed: e.target.checked })} data-testid="fill-confirmed-checkbox" />Pharmacy confirmed this fill
          </label>
          {f.confirmed && <Field label="Confirmation Source" required className="sm:col-span-2"><Select value={f.confirmation_source} onChange={(v) => setF({ ...f, confirmation_source: v })} placeholder="Select source" options={SRC} testid="fill-source-select" /></Field>}
          <Field label="Notes" className="sm:col-span-2"><textarea className="inp" rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="fill-notes-input" /></Field>
        </div>
        <p className="text-xs text-[var(--muted-ink)]">Unticked fills are saved as pending pharmacy confirmation; future dates are saved as scheduled. Both still count in Rx Filled, but not in Confirmed Dispensed.</p>
        <div className="flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy} data-testid="fill-cancel-btn">Cancel</button>
          <button className="btn btn-primary" onClick={() => save(false)} disabled={busy || !count} data-testid="fill-save-btn">{busy ? "Saving…" : `Save ${count} Fill${count === 1 ? "" : "s"}`}</button>
        </div>
        <AlertDialog open={!!existing} onOpenChange={(o) => !o && setExisting(null)}>
          <AlertDialogContent data-testid="existing-fill-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Existing Fill Found</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="text-sm">
                  <p className="mb-2">This patient already has a fill recorded for:</p>
                  <ul className="mb-2 list-disc pl-5" data-testid="existing-fill-list">{(existing || []).map((x) => <li key={x.id}><b>{x.medication}</b> — {fmtUS(x.fill_date)}</li>)}</ul>
                  <p>Update the existing record with the details in this form? No duplicate will be created; any other dates will be saved as new fills.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="existing-fill-cancel">Cancel</AlertDialogCancel>
              <button className="btn btn-ghost" onClick={() => { setExisting(null); onClose(); if (onViewExisting) onViewExisting(); }} data-testid="existing-fill-view">View Existing Record</button>
              <AlertDialogAction onClick={() => save(true)} data-testid="existing-fill-update">Update Existing Fill</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
